import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n, LANGS } from "../lib/i18n";
import { useCurrency } from "../lib/currency";
import Logo from "./Logo";
import { LogOut, LayoutDashboard, ShoppingBag, MessageCircle, Settings, UserCircle, PackagePlus, Briefcase } from "lucide-react";
import { hasPerm } from "../lib/perms";
import NotificationBell from "./NotificationBell";
import HeaderBalance from "./HeaderBalance";
import MaintenanceQuickToggle from "./MaintenanceQuickToggle";

const linkCls = "flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();

  const dashPath = user ? (user.role === "buyer" ? "/orders" : "/dashboard") : null;
  const isSupplier = user?.role === "supplier";
  const gated = !!user && user.two_factor_mandatory && !user.totp_enabled;
  const crowded = user && ["owner", "staff"].includes(user.role);
  const lbl = crowded ? "hidden min-[1800px]:inline whitespace-nowrap" : "hidden xl:inline whitespace-nowrap";

  return (
    <header className="sticky top-0 z-50 bg-[#101114]/85 backdrop-blur-xl border-b border-[#35353B]">
      <div className="h-20 flex items-center justify-between px-3 sm:px-6 lg:px-12">
        <Link to={isSupplier ? "/dashboard" : "/"} data-testid="header-logo-link" className="flex items-center shrink-0">
          <Logo size="sm" />
        </Link>

        <nav className={`flex items-center gap-0.5 ${crowded ? "lg:gap-1 min-[1800px]:gap-3" : "xl:gap-4"}`}>
          {!isSupplier && (
            <Link to="/marketplace" data-testid="nav-marketplace" className="hidden sm:flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">
              <ShoppingBag size={16} /> <span className={crowded ? lbl : ""}>{t("nav.marketplace")}</span>
            </Link>
          )}

          {user && !gated && (
            <>
              <Link to="/chat" data-testid="nav-chat" className="flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">
                <MessageCircle size={16} /> <span className={lbl}>{t("nav.chat")}</span>
              </Link>
              <Link to={dashPath} data-testid="nav-dashboard" className="flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">
                <LayoutDashboard size={16} /> <span className={lbl}>{user.role === "buyer" ? t("nav.orders") : t("nav.dashboard")}</span>
              </Link>
              {isSupplier && (
                <Link to="/supplier/submit" data-testid="nav-submit-stock" className={linkCls}>
                  <PackagePlus size={16} /> <span className={lbl}>{t("nav.submit_stock")}</span>
                </Link>
              )}
              {isSupplier && (
                <Link to="/jobs" data-testid="nav-jobs" className={linkCls}>
                  <Briefcase size={16} /> <span className={lbl}>Available Jobs</span>
                </Link>
              )}
              {hasPerm(user, "manage_demand") && (
                <Link to="/demand-board" data-testid="nav-demand-board" className={linkCls}>
                  <Briefcase size={16} /> <span className={lbl}>Demand Board</span>
                </Link>
              )}
              {user.role === "owner" && (
                <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">
                  <Settings size={16} /> <span className={lbl}>{t("nav.admin")}</span>
                </Link>
              )}
              {user.role === "owner" && <span className="hidden sm:flex"><MaintenanceQuickToggle /></span>}
              {user.role === "buyer" && <HeaderBalance />}
              <NotificationBell />
              <Link to="/account" data-testid="nav-account" title={user.email} className="flex items-center gap-1.5 text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">
                <UserCircle size={16} /> <span className={lbl}>{t("nav.account")}</span>
              </Link>
            </>
          )}
          {gated && (
            <Link to="/account" data-testid="nav-setup-2fa" className="text-xs text-[#F06464] border border-[#F06464] rounded-sm px-3 py-1.5">Aktifkan 2FA untuk membuka menu</Link>
          )}

          <select data-testid="lang-select" value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Language"
            className="bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] text-xs font-bold rounded-sm px-1.5 sm:px-2 py-1.5 outline-none focus:border-[#D4AF37]">
            {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {!isSupplier && (
            <button data-testid="currency-toggle" onClick={() => setCurrency(currency === "IDR" ? "USD" : "IDR")} title="IDR / USD"
              className={`${user && user.role !== "buyer" ? "hidden sm:block" : ""} bg-[#191A1F] border border-[#35353B] text-[#D4AF37] hover:border-[#D4AF37] text-xs font-bold rounded-sm px-2 py-1.5 transition-colors`}>
              {currency === "IDR" ? "Rp" : "$"}
            </button>
          )}

          {user ? (
            <button
              data-testid="header-logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-1.5 text-sm text-[#F06464] hover:text-white hover:bg-[#F06464] transition-colors border border-[#F06464] rounded-sm px-2 sm:px-3 py-1.5">
              <LogOut size={14} /> <span className={lbl}>{t("nav.logout")}</span>
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="whitespace-nowrap text-sm text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-1.5 sm:px-3 py-2">{t("nav.login")}</Link>
              <Link to="/register" data-testid="header-register-link" className="whitespace-nowrap bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] transition-colors font-bold rounded-sm px-3 sm:px-5 py-2 text-sm">{t("nav.register")}</Link>
            </>
          )}
        </nav>
      </div>
      {user?.role === "buyer" && <HeaderBalance mobile />}
    </header>
  );
}
